import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import DatagridCustom from "../components/General/DatagridCustom";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import AppDialog from "../components/General/Dialog/AppDialog";

import {
  getLegalFileStages,
  createLegalFileStage,
  updateLegalFileStage,
  recalculateLegalFileStages,
  forceImportLegalFileStages,
} from "../services/LegalFileStageService";
import { getOperationTypes } from "../services/OperationTypeService";
import { colorForStage } from "../config/legalFileStages";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const LEGAL_FILE_TYPES = [
  { value: "municipality", label: "Municipalidad" },
  { value: "external", label: "Comunes" },
];

// Devuelve la etapa a la que el tipo de operación está asignado para el tipo
// de expediente seleccionado (el back envía `stages` con pivot.legal_file_type).
const stageForType = (operationType, legalFileType) =>
  (operationType?.stages || []).find(
    (s) => s?.pivot?.legal_file_type === legalFileType,
  ) || null;

function StageDot({ color, size = 10 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

export default function SettingsLegalFileStages() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission("administrar etapas expediente");

  const [legalFileType, setLegalFileType] = useState("municipality");
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recalculating, setRecalculating] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  const [formOrder, setFormOrder] = useState("");
  const [formOperationTypes, setFormOperationTypes] = useState([]);
  const [operationTypeOptions, setOperationTypeOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [forceOpen, setForceOpen] = useState(false);
  const [forceStageId, setForceStageId] = useState("");
  const [forceFile, setForceFile] = useState(null);
  const [forcing, setForcing] = useState(false);
  const [forceError, setForceError] = useState("");
  const [forceResult, setForceResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getLegalFileStages(legalFileType);
      const data = (res.data?.data || [])
        .slice()
        .sort((a, b) => a.order - b.order);
      setStages(data);
    } catch {
      setError("Error al cargar las etapas.");
    } finally {
      setLoading(false);
    }
  }, [legalFileType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await recalculateLegalFileStages();
      showToast(res?.data?.message || "Expedientes reclasificados.", "success");
      await load();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "No se pudo recalcular.",
        "error",
      );
    } finally {
      setRecalculating(false);
    }
  };

  const loadOperationTypeOptions = async () => {
    setLoadingOptions(true);
    try {
      const res = await getOperationTypes();
      setOperationTypeOptions(res.data?.data || []);
    } catch {
      showToast("No se pudieron cargar los tipos de operación.", "error");
    } finally {
      setLoadingOptions(false);
    }
  };

  const openForce = () => {
    setForceStageId("");
    setForceFile(null);
    setForceError("");
    setForceResult(null);
    setForceOpen(true);
  };

  const handleForceSubmit = async () => {
    if (!forceStageId) {
      setForceError("Elegí la etapa a forzar.");
      return;
    }
    if (!forceFile) {
      setForceError("Subí el Excel con los códigos de expediente.");
      return;
    }

    setForcing(true);
    setForceError("");
    setForceResult(null);
    try {
      const res = await forceImportLegalFileStages(forceFile, forceStageId);
      setForceResult(res?.data?.data || null);
      showToast(res?.data?.message || "Etapas forzadas.", "success");
      await load();
    } catch (e) {
      const errors = e?.response?.data?.errors;
      const firstError = errors ? Object.values(errors)[0]?.[0] : null;
      setForceError(
        firstError || e?.response?.data?.message || "No se pudo forzar la etapa.",
      );
    } finally {
      setForcing(false);
    }
  };

  const openCreate = async () => {
    setEditing(null);
    setFormName("");
    setFormOrder("");
    setFormOperationTypes([]);
    setFormError("");
    setDialogOpen(true);
    await loadOperationTypeOptions();
  };

  const openEdit = async (row) => {
    setEditing(row);
    setFormName(row.name);
    setFormOrder(String(row.order));
    setFormError("");
    setDialogOpen(true);
    await loadOperationTypeOptions();
    setFormOperationTypes(row.operation_types || []);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError("Completá el nombre de la etapa.");
      return;
    }
    if (formOperationTypes.length === 0) {
      setFormError(
        "Seleccioná al menos un tipo de operación que impulse esta etapa. Si queda vacía, después no se va a poder clasificar ningún expediente en ella.",
      );
      return;
    }

    const payload = {
      name: formName.trim(),
      legal_file_type: legalFileType,
      operation_type_ids: formOperationTypes.map((ot) => ot.id),
    };
    if (formOrder !== "") {
      payload.order = Number(formOrder);
    }

    setSaving(true);
    setFormError("");
    try {
      if (editing) {
        await updateLegalFileStage(editing.id, payload);
      } else {
        await createLegalFileStage(payload);
      }
      setDialogOpen(false);
      await load();
      showToast(editing ? "Etapa actualizada." : "Etapa creada.", "success");
    } catch (e) {
      const errors = e?.response?.data?.errors;
      const firstError = errors ? Object.values(errors)[0]?.[0] : null;
      setFormError(
        firstError || e?.response?.data?.message || "Error al guardar.",
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      field: "order",
      headerName: "Orden",
      flex: 0.5,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}
        >
          <StageDot color={colorForStage(params.row)} />
          <Typography sx={{ fontWeight: 800 }}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Etapa",
      flex: 2,
      minWidth: 220,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}
        >
          <Typography sx={{ fontWeight: 800 }}>{params.value}</Typography>
          {params.row.is_default && (
            <Chip
              icon={<LockRoundedIcon sx={{ fontSize: "14px !important" }} />}
              label="Por defecto"
              size="small"
              sx={{ height: 22, fontSize: 11, fontWeight: 800 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "operation_types",
      headerName: "Tipos de operación",
      flex: 1.2,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const items = params.value || [];
        if (items.length === 0) {
          return "Ninguno";
        }
        return (
          <Chip
            label={`${items.length} tipo${items.length === 1 ? "" : "s"}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: 12 }}
          />
        );
      },
    },
    {
      field: "legal_files_count",
      headerName: "Expedientes",
      flex: 0.8,
      renderCell: (params) => params.value ?? 0,
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.5,
      sortable: false,

      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => openEdit(params.row)}
          disabled={!canManage || params.row.is_default}
          title={
            params.row.is_default
              ? "La etapa por defecto no se puede modificar"
              : "Editar etapa"
          }
        >
          <EditRoundedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<TimelineRoundedIcon />}
            title="Etapas de Expediente"
            description="Definí qué tipos de operación hacen que un expediente pase a cada etapa. Las etapas son comunes, pero los tipos que las impulsan se configuran por separado para Municipalidad y Comunes."
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    recalculating ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshRoundedIcon />
                    )
                  }
                  onClick={handleRecalculate}
                  disabled={!canManage || recalculating}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Recalcular expedientes
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileRoundedIcon />}
                  onClick={openForce}
                  disabled={!canManage}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Forzar por Excel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={openCreate}
                  disabled={!canManage}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Nueva etapa
                </Button>
              </Box>
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, opacity: 0.7 }}>
              Tipo de expediente
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={legalFileType}
              onChange={(_, v) => v && setLegalFileType(v)}
              disabled={loading}
            >
              {LEGAL_FILE_TYPES.map((t) => (
                <ToggleButton
                  key={t.value}
                  value={t.value}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    px: 2,
                    borderRadius: 2,
                  }}
                >
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <DatagridCustom
            rows={stages}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            error={error}
            rowHeight={56}
          />
        </Grid>

        <AppDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={editing ? `Editar etapa "${editing.name}"` : "Nueva etapa"}
          maxWidth="sm"
          fullWidth
          stickyHeader
          actions={
            <>
              <Button onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: 2, fontWeight: 900 }}
              >
                {saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Guardar"
                )}
              </Button>
            </>
          }
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}
          >
            {formError && (
              <Box sx={{ color: "error.main", fontSize: 13, fontWeight: 700 }}>
                {formError}
              </Box>
            )}

            <Box sx={{ fontSize: 12, opacity: 0.7 }}>
              Configurando los tipos de operación para expedientes{" "}
              <strong>
                {LEGAL_FILE_TYPES.find((t) => t.value === legalFileType)?.label}
              </strong>
              . Los tipos de operación del otro tipo de expediente no se modifican.
            </Box>

            <TextField
              label="Nombre"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              size="small"
              autoFocus
            />

            <TextField
              label="Orden"
              type="number"
              value={formOrder}
              onChange={(e) => setFormOrder(e.target.value)}
              size="small"
              slotProps={{ htmlInput: { min: 1 } }}
              helperText={
                editing
                  ? "Si coincide con el de otra etapa, esa y las siguientes se corren automáticamente. La etapa por defecto siempre queda primera."
                  : "Dejalo vacío para agregarla al final. Si coincide con el de otra etapa, las demás se corren automáticamente."
              }
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              loading={loadingOptions}
              options={operationTypeOptions}
              value={formOperationTypes}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              onChange={(_, newValue) => setFormOperationTypes(newValue)}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props;
                const currentStage = stageForType(option, legalFileType);
                const belongsElsewhere =
                  currentStage && (!editing || currentStage.id !== editing.id);
                return (
                  <li key={key} {...rest}>
                    <Checkbox
                      icon={checkboxIcon}
                      checkedIcon={checkboxCheckedIcon}
                      checked={selected}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5 }} noWrap>
                        {option.name}
                      </Typography>
                      {belongsElsewhere && (
                        <Typography sx={{ fontSize: 11, opacity: 0.6 }}>
                          Actualmente en: {currentStage.name}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipos de operación que impulsan esta etapa"
                  placeholder="Buscar…"
                  size="small"
                  helperText="Cuando la operación más reciente de un expediente coincida con alguno de estos tipos, el expediente pasa a esta etapa. No puede quedar vacío."
                />
              )}
            />
          </Box>
        </AppDialog>

        <AppDialog
          open={forceOpen}
          onClose={() => setForceOpen(false)}
          title="Forzar etapa por Excel"
          maxWidth="sm"
          fullWidth
          stickyHeader
          actions={
            <>
              <Button onClick={() => setForceOpen(false)} disabled={forcing}>
                Cerrar
              </Button>
              <Button
                variant="contained"
                onClick={handleForceSubmit}
                disabled={forcing}
                sx={{ borderRadius: 2, fontWeight: 900 }}
              >
                {forcing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Forzar etapa"
                )}
              </Button>
            </>
          }
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}
          >
            {forceError && (
              <Box sx={{ color: "error.main", fontSize: 13, fontWeight: 700 }}>
                {forceError}
              </Box>
            )}

            <Box sx={{ fontSize: 12, opacity: 0.7 }}>
              Subí un Excel/CSV con los <strong>códigos de expediente en la
              primera columna</strong>. Esos expedientes quedan fijos en la etapa
              elegida y el recálculo automático no los vuelve a mover.
            </Box>

            <TextField
              select
              label="Etapa a forzar"
              value={forceStageId}
              onChange={(e) => setForceStageId(e.target.value)}
              size="small"
            >
              {stages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              {forceFile ? forceFile.name : "Elegir archivo (.xlsx, .xls, .csv)"}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  setForceFile(e.target.files?.[0] || null);
                  setForceResult(null);
                }}
              />
            </Button>

            {forceResult && (
              <Box sx={{ fontSize: 13 }}>
                <Box sx={{ fontWeight: 800, color: "success.main" }}>
                  {forceResult.forced} expediente(s) forzado(s).
                </Box>
                {forceResult.not_found?.length > 0 && (
                  <Box sx={{ mt: 0.5, opacity: 0.75 }}>
                    Sin coincidencia ({forceResult.not_found.length}):{" "}
                    {forceResult.not_found.join(", ")}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </AppDialog>
      </Grid>
    </Container>
  );
}
